import { OutlineButton } from "@components/Buttons";
import { useTheme } from "@hooks/useTheme";
import { useTranslation } from "react-i18next";
import { basename } from "../../router";
import useEscapeKey from "@hooks/useEscapeKey";

const AboutModal = ({
  isOpen,
  onClose,
  systemInfo,
  loading,
  error,
  "data-testid": dataTestId = "about-modal",
}) => {
  const { t } = useTranslation();
  const { colors } = useTheme();

  useEscapeKey(onClose, isOpen);

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 ${colors.bgBlurModal} z-50`}
        data-testid={`${dataTestId}-backdrop`}
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid={`${dataTestId}-wrapper`}>
        <div
          className={`${colors.bgPrimary} rounded-lg shadow-2xl w-[560px] h-[332px] mx-4 flex flex-col`}
          data-testid={dataTestId}
        >
          {/* Header */}
          <div className={`flex-shrink-0 border-b px-6 py-4 ${colors.borderPrimary}`} data-testid={`${dataTestId}-header`}>
            <h2 className={`text-center text-xl font-bold uppercase tracking-wide ${colors.textPrimary}`} data-testid={`${dataTestId}-title`}>
              {t("common.about-title")}
            </h2>
          </div>

          {/* Content */}
          <div className="px-6 py-4 flex-1 overflow-hidden" data-testid={`${dataTestId}-content`}>
            {loading ? (
              <div className="flex items-center justify-center h-full" data-testid={`${dataTestId}-loading`}>
                <div className={`h-8 w-8 animate-spin rounded-full border-b-2 ${colors.borderLoader}`} data-testid={`${dataTestId}-loading-spinner`}></div>
              </div>
            ) : error ? (
              <div
                className={`flex h-full items-center justify-center text-center ${colors.textDangerLight}`}
                data-testid={`${dataTestId}-error`}
              >
                {t("common.errorLoadingSystemInfo")}
              </div>
            ) : (
              <>
                {/* Content Area with lighter background and border */}
                <div
                  className={`flex h-full flex-col justify-between rounded-lg border p-4 ${colors.bgInput} ${colors.borderPrimary}`}
                  data-testid={`${dataTestId}-body`}
                >
                  {/* System Info */}
                  <div className="space-y-3">
                    {/* Additional Info Link */}
                    <div className="text-center" data-testid={`${dataTestId}-field-system-link`}>
                      <a
                        href={`${basename}/NOTICE_INFO_COMPONENT_SYSTEM.html`}
                        className={`text-sm underline transition-colors duration-200 ${colors.textActive} ${colors.textHoverActive}`}
                        rel="noopener noreferrer"
                        target="_blank"
                        data-testid={`${dataTestId}-field-system-link-anchor`}
                        onClick={(e) => {
                          e.preventDefault();
                          window.open(`${basename}/NOTICE_INFO_COMPONENT_SYSTEM.html`, "_blank", "noopener,noreferrer");
                        }}
                      >
                        {t("common.aboutSystemLink")}
                      </a>
                    </div>
                    <div className="flex items-center justify-center gap-2" data-testid={`${dataTestId}-field-system-version`}>
                      <span className={`text-sm font-medium ${colors.textMessage}`} data-testid={`${dataTestId}-field-system-version-label`}>{t("common.systemVersion")}:</span>
                      <span className={`text-sm font-medium ${colors.textMessage}`} data-testid={`${dataTestId}-field-system-version-value`}>
                        {systemInfo?.systemVersion || "1.0.0"}
                      </span>
                    </div>

                    <div className="flex items-center justify-center gap-2" data-testid={`${dataTestId}-field-license-left-days`}>
                      <span className={`text-sm font-medium ${colors.textMessage}`} data-testid={`${dataTestId}-field-license-left-days-label`}>
                        {t("common.licenseLeftDays")}:
                      </span>
                      <span className={`text-sm font-medium ${colors.textMessage}`} data-testid={`${dataTestId}-field-license-left-days-value`}>
                        {systemInfo?.licenseLeftDays || "0"}
                      </span>
                    </div>

                    <div className="flex items-center justify-center gap-2" data-testid={`${dataTestId}-field-license-max-users`}>
                      <span className={`text-sm font-medium ${colors.textMessage}`} data-testid={`${dataTestId}-field-license-max-users-label`}>
                        {t("common.licenseMaxUsers")}:
                      </span>
                      <span className={`text-sm font-medium ${colors.textMessage}`} data-testid={`${dataTestId}-field-license-max-users-value`}>
                        {systemInfo?.licenseMaxUsers || "0"}
                      </span>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Footer */}
          <div className={`flex flex-shrink-0 justify-center border-t px-6 py-4 ${colors.borderPrimary}`} data-testid={`${dataTestId}-footer`}>
            <OutlineButton data-testid={`${dataTestId}-close-button`} onClick={onClose}>{t("common.close-button")}</OutlineButton>
          </div>
        </div>
      </div>
    </>
  );
};

export default AboutModal;
